import { ModalProps, useModals, Modal, Button, Select } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import {
  ApiParticipantChartAxisType,
  ApiPerformanceReviewParticipantChartSetting,
} from "@/types/performance-review.types.ts";
import { Controller, FieldError, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import i18n from "i18next";
import { AxisOption } from "@/pages/talent/performance-reviews/view/use-chart-setting.hook.tsx";

const buildChartSettingsSchema = () =>
  yup.object({
    x_axis: yup.object({
      type: yup
        .mixed<ApiParticipantChartAxisType>()
        .oneOf(Object.values(ApiParticipantChartAxisType))
        .required(i18n.t("errors:field_required")),
      reviewer_type_id: yup
        .number()
        .nullable()
        .when("type", {
          is: ApiParticipantChartAxisType.reviewerType,
          then: (schema) => schema.required(i18n.t("errors:field_required")),
          otherwise: (schema) => schema.nullable(),
        }),
    }),
    y_axis: yup.object({
      type: yup
        .mixed<ApiParticipantChartAxisType>()
        .oneOf(Object.values(ApiParticipantChartAxisType))
        .required(i18n.t("errors:field_required")),
      reviewer_type_id: yup
        .number()
        .nullable()
        .when("type", {
          is: ApiParticipantChartAxisType.reviewerType,
          then: (schema) => schema.required(i18n.t("errors:field_required")),
          otherwise: (schema) => schema.nullable(),
        }),
    }),
  });

export interface PerformanceReviewInsightConfigureChartModalProps extends ModalProps {
  options: AxisOption[];
  setting: ApiPerformanceReviewParticipantChartSetting;
  onSubmit: (setting: ApiPerformanceReviewParticipantChartSetting) => void;
}

export function PerformanceReviewInsightConfigureChartModal({
  options,
  setting,
  onSubmit,
  ...props
}: PerformanceReviewInsightConfigureChartModalProps) {
  const { t } = useTranslation();
  const { id } = props;
  const { close } = useModals();

  const chartSettingsSchema = buildChartSettingsSchema();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ApiPerformanceReviewParticipantChartSetting>({
    mode: "onSubmit",
    resolver: yupResolver(chartSettingsSchema),
    defaultValues: setting,
  });

  const saveSetting = handleSubmit((values) => {
    onSubmit(values);
    close(id);
  });

  const getAxisError = (field: "x_axis" | "y_axis") =>
    (errors?.[field]?.type as FieldError)?.message ??
    (errors?.[field]?.reviewer_type_id as FieldError)?.message;

  const renderAxisSelect = (name: "x_axis" | "y_axis", label: string) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Select
          label={label}
          variant="default"
          options={options}
          emptyTitle={t("common:select")}
          value={
            field.value
              ? options.find(
                  (opt) =>
                    opt.type === field.value.type &&
                    opt.reviewer_type_id === field.value.reviewer_type_id
                )?.id
              : undefined
          }
          error={getAxisError(name)}
          onChange={(_, entity) => {
            if (entity) {
              field.onChange({
                type: entity.type,
                reviewer_type_id: entity.reviewer_type_id,
              });
            }
          }}
        />
      )}
    />
  );

  return (
    <Modal
      title={t("performance_review:configure_chart")}
      variant="side"
      layout="stretch"
      withCloser
      actions={
        <Button size="lg" className="w-full" onClick={saveSetting}>
          {t("common:apply")}
        </Button>
      }
      {...props}
    >
      {renderAxisSelect("x_axis", t("performance_review:x_axis"))}
      {renderAxisSelect("y_axis", t("performance_review:y_axis"))}
    </Modal>
  );
}
